import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, Smartphone, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import CustomerOnboardingDialog from "../components/CustomerOnboardingDialog";

const WIDGET_ID = process.env.REACT_APP_MSG91_WIDGET_ID;
const TOKEN_AUTH = process.env.REACT_APP_MSG91_TOKEN_AUTH;

const MSG91_SCRIPT = "https://verify.msg91.com/otp-provider.js";

const normalizeMobile = (value) => {
  const digits = value.replace(/\D/g, "");

  if (digits.startsWith("91") && digits.length === 12) {
    return digits;
  }

  if (digits.length === 10) {
    return `91${digits}`;
  }

  return null;
};

const CustomerLogin = () => {
  const navigate = useNavigate();
  const { loginWithOTP, updateCustomer } = useCustomerAuth();

  const [mobile, setMobile] = useState("");
  const [onboardingCustomer, setOnboardingCustomer] = useState(null);
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("mobile");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [scriptReady, setScriptReady] = useState(false);
  const [reqId, setReqId] = useState(null);

  const scriptRef = useRef(null);

  useEffect(() => {
    if (!WIDGET_ID || !TOKEN_AUTH) {
      console.error("MSG91 widget configuration is missing");
      return;
    }

    if (window.sendOtp && window.verifyOtp) {
      setScriptReady(true);
      return;
    }

    const script = document.createElement("script");
    script.src = MSG91_SCRIPT;
    script.async = true;

    script.onload = () => {
      try {
        window.initSendOTP({
          widgetId: WIDGET_ID,
          tokenAuth: TOKEN_AUTH,
          identifier: "",
          exposeMethods: true,
          captchaRenderId: "",
          success: (data) => {
            console.log("MSG91 widget initialized successfully");
            console.log("MSG91 success response received");
          },
          failure: (error) => {
            console.error("MSG91 widget initialization failed:", error);
          },
        });

        setScriptReady(true);
      } catch (error) {
        console.error("MSG91 initialization failed:", error);
        toast.error("OTP service could not be initialized.");
      }
    };

    script.onerror = () => {
      toast.error("Unable to load OTP service.");
    };

    document.body.appendChild(script);
    scriptRef.current = script;

    return () => {
      if (scriptRef.current?.parentNode) {
        scriptRef.current.parentNode.removeChild(scriptRef.current);
      }
    };
  }, []);

  const handleSendOTP = () => {
    const normalized = normalizeMobile(mobile);

    if (!normalized) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }

    if (!scriptReady || !window.sendOtp) {
      toast.error("OTP service is still loading. Please try again.");
      return;
    }

    setLoading(true);

    window.sendOtp(
      normalized,
      (data) => {
        console.log("MSG91 OTP sent:", data);

        if (typeof data === "object" && data?.message) {
          const newReqId = data.message;
          setReqId(newReqId);

          // Bind this MSG91 OTP request to the entered mobile.
          fetch(`${process.env.REACT_APP_API_URL || ""}/api/customer-auth/session`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              mobile,
              request_id: newReqId,
            }),
          })
            .then(async (response) => {
              if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                  errorData.detail || "Could not create OTP session"
                );
              }
              return response.json();
            })
            .then(() => {
              setStep("otp");
              setLoading(false);
              toast.success("OTP sent successfully.");
            })
            .catch((error) => {
              console.error("PartVision OTP session error:", error);
              setLoading(false);
              toast.error(
                error.message || "Unable to initialize OTP session."
              );
            });

          return;
        }

        setStep("otp");
        setLoading(false);
        toast.success("OTP sent successfully.");
      },
      (error) => {
        console.error("MSG91 send OTP error:", error);
        setLoading(false);
        toast.error(
          error?.message || "Unable to send OTP. Please try again."
        );
      }
    );
  };

  const handleVerifyOTP = () => {
    const cleanOtp = otp.replace(/\D/g, "");

    if (cleanOtp.length < 4) {
      toast.error("Please enter the OTP.");
      return;
    }

    if (!window.verifyOtp) {
      toast.error("OTP service is not ready.");
      return;
    }

    setLoading(true);

    window.verifyOtp(
      cleanOtp,
      async (data) => {
        console.log("MSG91 OTP verified:", data);

        const accessToken =
          data?.["access-token"] ||
          data?.access_token ||
          data?.token ||
          data?.jwt ||
          data?.message ||
          data?.data?.["access-token"] ||
          data?.data?.access_token ||
          data?.data?.token ||
          data?.data?.jwt ||
          data?.data?.message;

        if (!accessToken) {
          console.error("MSG91 response without access token:", data);
          setLoading(false);
          toast.error("OTP verified but login token was not received.");
          return;
        }

        try {
          const loggedInCustomer = await loginWithOTP(
            mobile,
            accessToken,
            reqId
          );

          toast.success("Login successful.");

          if (loggedInCustomer?.profile_completed) {
            navigate("/", { replace: true });
          } else {
            setOnboardingCustomer(loggedInCustomer);
          }
        } catch (error) {
          console.error("PartVision customer login failed:", error);
          toast.error(
            error.response?.data?.detail ||
              "Login failed. Please try again."
          );
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error("MSG91 verify OTP error:", error);
        setLoading(false);
        toast.error(
          error?.message || "Invalid OTP. Please try again."
        );
      },
      reqId || undefined
    );
  };

  const handleRetryOTP = () => {
    if (!window.retryOtp) {
      toast.error("OTP service is not ready.");
      return;
    }

    setResending(true);

    window.retryOtp(
      null,
      (data) => {
        console.log("MSG91 OTP resent:", data);

        if (typeof data === "object" && data?.message) {
          setReqId(data.message);
        }

        setResending(false);
        toast.success("OTP resent successfully.");
      },
      (error) => {
        console.error("MSG91 retry error:", error);
        setResending(false);
        toast.error(
          error?.message || "Unable to resend OTP."
        );
      },
      reqId || undefined
    );
  };

  const goBack = () => {
    setOtp("");
    setReqId(null);
    setStep("mobile");
  };

  return (
    <div className="min-h-[75vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="border rounded-xl bg-card shadow-sm overflow-hidden">
          <div className="p-7 sm:p-8">

            <div className="flex justify-center mb-5">
              <div className="w-14 h-14 rounded-full bg-brand/10 flex items-center justify-center">
                {step === "mobile" ? (
                  <Smartphone className="w-7 h-7 text-brand" />
                ) : (
                  <ShieldCheck className="w-7 h-7 text-brand" />
                )}
              </div>
            </div>

            <div className="text-center mb-7">
              <h1 className="text-2xl font-heading font-black">
                {step === "mobile"
                  ? "Login to PartVision"
                  : "Verify your mobile"}
              </h1>

              <p className="text-sm text-muted-foreground mt-2">
                {step === "mobile"
                  ? "Enter your mobile number to continue"
                  : `Enter the OTP sent to +${mobile.replace(/\D/g, "")}`}
              </p>
            </div>

            {step === "mobile" ? (
              <div className="space-y-5">
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    Mobile Number
                  </label>

                  <div className="flex mt-2 border rounded-md overflow-hidden">
                    <div className="px-3 flex items-center bg-secondary text-sm font-medium border-r">
                      +91
                    </div>

                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={mobile.replace(/\D/g, "").slice(-10)}
                      onChange={(e) =>
                        setMobile(
                          e.target.value.replace(/\D/g, "").slice(-10)
                        )
                      }
                      placeholder="Enter 10-digit mobile"
                      className="flex-1 px-3 py-3 bg-transparent outline-none text-sm"
                      autoFocus
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleSendOTP}
                  disabled={loading || !scriptReady}
                  className="w-full h-11 rounded-md bg-foreground text-background font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Smartphone className="w-4 h-4" />
                  )}
                  {loading ? "Sending OTP..." : "Send OTP"}
                </button>

                {!scriptReady && (
                  <p className="text-center text-xs text-muted-foreground">
                    Initializing secure OTP service...
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-5">
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    One-Time Password
                  </label>

                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={otp}
                    onChange={(e) =>
                      setOtp(
                        e.target.value.replace(/\D/g, "").slice(0, 6)
                      )
                    }
                    placeholder="Enter OTP"
                    className="w-full mt-2 border rounded-md px-4 py-3 text-center text-xl tracking-[0.4em] bg-transparent outline-none"
                    autoFocus
                  />
                </div>

                <button
                  type="button"
                  onClick={handleVerifyOTP}
                  disabled={loading || otp.length < 4}
                  className="w-full h-11 rounded-md bg-foreground text-background font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading && (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  {loading ? "Verifying..." : "Verify & Continue"}
                </button>

                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={goBack}
                    disabled={loading}
                    className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Change number
                  </button>

                  <button
                    type="button"
                    onClick={handleRetryOTP}
                    disabled={resending || loading}
                    className="font-medium text-brand hover:underline disabled:opacity-50"
                  >
                    {resending ? "Resending..." : "Resend OTP"}
                  </button>
                </div>
              </div>
            )}

            <div className="mt-7 pt-5 border-t text-center text-[11px] text-muted-foreground">
              Secure OTP verification powered by MSG91
            </div>
          </div>
        </div>
      </div>
      {onboardingCustomer && (
        <CustomerOnboardingDialog
          customer={onboardingCustomer}
          onComplete={(updatedCustomer) => {
            updateCustomer(updatedCustomer);
            setOnboardingCustomer(null);
            navigate("/", { replace: true });
          }}
        />
      )}
    </div>
  );
};

export default CustomerLogin;
